import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { Router } from '@angular/router';
import { EYesNo } from '../../../shared/models/types.enum';
import { DateUtils } from '../../../shared/helpers/dateUtils';
import { StudentService } from '../../../shared/services/student.service';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { PopupMessageComponent } from '../popup-message/popup-message.component';

@Component({
  selector: 'student-info-form',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent,
    PopupMessageComponent
  ],
  templateUrl: './student-info-form.component.html',
  styleUrl: './student-info-form.component.css'
})
export class StudentInfoFormComponent implements OnInit, OnChanges {
  icons = Icons;
  yesNo = EYesNo;
  @ViewChild('datepickerBirthdate') datepickerBirthdate!: ElementRef;

  @Input() ssidNum: string | null = null;
  @Output() onClose = new EventEmitter<void>();

  studentInfoForm: FormGroup = new FormGroup({});
  userId: string | null = null;
  buildingId: string | null = null;
  regionId: string | null = null;
  popupMessage: string | null = null;
  messageModal: string | null = null;

  loading: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private studentService: StudentService,
    private userDataService: UserDataService,
  ) {}

  ngOnInit() {
    this.buildingId = this.userDataService.getBuildingId();
    this.regionId = this.userDataService.getRegionId();

    this.studentInfoForm = new FormGroup({
      student_identifier: new FormControl(this.ssidNum, Validators.required),
      first_name: new FormControl('', Validators.required),
      last_name: new FormControl('', Validators.required),
      birthday: new FormControl('2010-01-01', Validators.required),
      grade: new FormControl(this.calculateGrade('2010-01-01'), Validators.required),
      identified_gender: new FormControl('male', Validators.required),
      physical_description: new FormControl('', Validators.required),
      has_iep: new FormControl('0', Validators.required),
      iep_contact: new FormControl({value: '', disabled: true}),
      has_ihp: new FormControl('0', Validators.required),
      has_504: new FormControl('0', Validators.required),
    });

    this.onChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ssidNum'] && changes['ssidNum'].currentValue) {
      this.studentInfoForm.get('student_identifier')?.setValue(changes['ssidNum'].currentValue);
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerBirthdate) {
        new Datepicker(this.datepickerBirthdate.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        // set default value for dob
        this.datepickerBirthdate.nativeElement.value = DateUtils.formatDateForDisplay('2010-01-01');
        this.datepickerBirthdate.nativeElement.addEventListener('changeDate', (event: any) => {
          this.studentInfoForm.get('birthday')?.setValue(event.target.value);
        });
        this.datepickerBirthdate.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.studentInfoForm.get('birthday')?.setValue(formattedDate);
        });
      }
    }
  }

  onChanges(): void {
    this.studentInfoForm.get('has_iep')!.valueChanges.subscribe(val => {
      const iepContactControl = this.studentInfoForm.get('iep_contact');
      if (val === this.yesNo.Yes) {
        iepContactControl?.enable();
        iepContactControl?.addValidators(Validators.required);
      } else {
        iepContactControl?.disable();
        iepContactControl?.clearValidators();
      }
      this.cdRef.detectChanges();
    });
    this.studentInfoForm.get('birthday')?.valueChanges.subscribe(dob => {
      if (dob) {
        const grade = this.calculateGrade(dob);
        this.studentInfoForm.get('grade')?.setValue(grade);
      }
    });
  }

  handleCloseStudentInfo(): void {
    this.onClose.emit();
  }

  saveStudentInfo(): void {
    if (this.studentInfoForm.valid && this.buildingId && this.regionId) {
      this.loading = true;
      // get all value of the form
      const formValue = this.studentInfoForm.value;
      // cast to number typoe if needed:
        formValue.grade = Number(formValue.grade);
        formValue.has_iep = Number(formValue.has_iep);
        formValue.has_ihp = Number(formValue.has_ihp);
        formValue.has_504 = Number(formValue.has_504);

        formValue.building_id = Number(this.buildingId);
        this.studentService.createStudent(this.regionId, formValue).subscribe({
          next: (response) => {
            this.loading = false;
            this.router.navigate(['/evaluations/form/' + response.case_id]);
          },
          error: (error) => {
            this.loading = false;
            console.error('Error creating student:', error.message);
            this.messageModal = 'unsuccess';
            this.popupMessage = error.message;
          }
        });
    }
  }

  private calculateGrade(dob: string): string {
    const cutoffMonth = 9;
    const cutoffDay = 1;
    const dobDate = new Date(dob);
    const cutoffDate = new Date(dobDate.getFullYear() + 6, cutoffMonth - 1, cutoffDay);
    const today = new Date();
    let grade = today.getFullYear() - cutoffDate.getFullYear();
    if (today.getMonth() < cutoffDate.getMonth() || (today.getMonth() === cutoffDate.getMonth() && today.getDate() < cutoffDate.getDate())) {
      grade--;
    }
    return (grade >= 1 && grade <= 12) ? `${grade}` : '0';
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
